import { SelectWithAdd } from "./Common/SelectWithAdd.tsx";
import {
    setTeamPresets,
    getTeamPresets,
    setProfilePresetName
} from "@store/actions/team.actions.js";
import { useState, useEffect } from "react";
import { trainingAPI } from "@api/TrainingAPI.js";
import { useDispatch } from "react-redux";
import { Tooltip, Button, Select } from "antd";
import { User } from "@auth0/auth0-react";

export enum PresetType {
    ForcePlate = "force-plate-tile",
    Gps = "gps-tile",
    Profile = "profile-tile",
    Testing = "testing-tile",
    Overview = "overview-tile",
    Nutrition = "nutrition-tile",
    CustomBreakdownReportMetrics = "custom-breakdown-report-metrics",
    GpsReportMetrics = 'gps-report-metrics',
    TeamReportMetrics = 'team-report-metrics',
    PlayerReportMetrics = 'player-report-metrics',
    TestingReportMetrics = 'testing-report-metrics',
    ForcePlateReportMetrics = 'forceplate-report-metrics',
    ReportSettings = 'report-settings'
}

interface IPresetSelection {
    presets: IPreset[],
    user: User,
    presetType: PresetType,
    statDefaults: string[],
    currStatMetrics: string[],
    setStatMetrics: React.Dispatch<string[]>,
    currentSelectedTilePreset: string,
    setCurrentSelectedTilePreset: React.Dispatch<string>,
    comparisonTypes: IComparisonType[],
    comparisonOption: string,
    setComparisonOption: React.Dispatch<string>,
    handleSave: () => void,
    warnUnsavedChanges: boolean,
    setWarnUnsavedChages: React.Dispatch<boolean>,
}

export const PresetSelection = ({
    presets,
    user,
    presetType,
    statDefaults,
    currStatMetrics,
    setStatMetrics,
    currentSelectedTilePreset,
    setCurrentSelectedTilePreset,
    comparisonTypes,
    comparisonOption,
    setComparisonOption,
    handleSave,
    warnUnsavedChanges = false,
    setWarnUnsavedChages
}: IPresetSelection) => {
    const dispatch = useDispatch();
    const DEFAULT_PRESET = 'Default'

    const getAllPresets = (updatedPreset?: IPreset[]) => {
        return [{
            label: DEFAULT_PRESET,
            value: DEFAULT_PRESET,
            disabled: true,
        }].concat((updatedPreset || presets)?.map((preset) => {
            return {
                label: preset.name,
                value: preset.name,
                disabled: preset.creator_id__email !== user.email,
            }
        }))
    }

    const addTileSetting = async (item: string) => {
        const newPreset = await trainingAPI.createPreset({ name: item, type: presetType, config: currStatMetrics });
        dispatch(setTeamPresets(newPreset))

        setTilePresets(getAllPresets(newPreset))
        setCurrentSelectedTilePreset(item)
    }

    const deleteTileSetting = async (item: string) => {
        const presetOfInterest = presets?.find((preset) => preset.name === item)
        await trainingAPI.deletePreset(presetOfInterest?.preset_id)

        const updatedPreset = presets.filter(preset => preset.preset_id !== presetOfInterest?.preset_id)
        dispatch(setTeamPresets(updatedPreset))

        setTilePresets(getAllPresets(updatedPreset))
        setCurrentSelectedTilePreset(DEFAULT_PRESET)
    }

    const getTooltipText = () => {
        if (getPresetSelected()?.creator_id__email !== user.email && currentSelectedTilePreset !== DEFAULT_PRESET) {
            return 'Cannot modify tiles created by another user'
        }

        if (warnUnsavedChanges) {
            return 'You have unsaved changes'
        }

        return undefined
    }

    const handleSaveLocal = () => {
        setWarnUnsavedChages(false)
        handleSave()
    }

    const [tilePresets, setTilePresets] = useState(getAllPresets())
    const getPresetSelected = () => presets?.find((preset) => preset.name === currentSelectedTilePreset)

    useEffect(() => {
        setTilePresets(getAllPresets())
    }, [presets])

    useEffect(() => {
        if (currentSelectedTilePreset === DEFAULT_PRESET) {
            setStatMetrics(statDefaults);
        } else {
            setStatMetrics(getPresetSelected()?.config || [])
        }
    }, [statDefaults, currentSelectedTilePreset]);

    useEffect(() => {
        dispatch(getTeamPresets({ type: [presetType] }) as any)
    }, [])

    useEffect(() => {
        dispatch(setProfilePresetName(currentSelectedTilePreset))
    }, [currentSelectedTilePreset])

    return (
        <>
            <div style={{ width: '40%', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                <div style={{ flexGrow: 1 }}>
                    <SelectWithAdd
                        items={tilePresets}
                        addItem={addTileSetting}
                        placeholder={DEFAULT_PRESET}
                        deleteItem={deleteTileSetting}
                        currentSelectedTilePreset={currentSelectedTilePreset}
                        setValue={setCurrentSelectedTilePreset}
                        addItemLabel={'Add'}
                        addItemTextboxLabel={'Enter Preset Name'}
                        label={'Select Preset'} />
                </div>

                <div>
                    <Tooltip title={getTooltipText() && getTooltipText()}>
                        <Button type="primary" size="small"
                            onClick={handleSaveLocal}
                            disabled={getPresetSelected()?.creator_id__email !== user.email && currentSelectedTilePreset !== DEFAULT_PRESET}
                            style={{ width: "100px" }}
                            className={warnUnsavedChanges ? 'pulse' : ''}
                        >
                            Save Tiles
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <div style={{ width: '33%', display: 'flex' }}>
                <div className="exportable tab-testing testing-stats testing-stats-toggles flex gap-16 mb-16 items-center w-full justify-center" style={{ flexBasis: '33.33%' }}>
                    <Select
                        options={comparisonTypes}
                        value={comparisonOption}
                        showSearch
                        optionFilterProp="value"
                        onChange={setComparisonOption}
                        className={`forceplate-stat-comparison`}
                        style={{ width: 300 }}
                    />
                </div>
            </div>
        </>
    )
}

interface IPreset {
    config: string[],
    created_at: string,
    creator_id__email: string,
    name: string,
    preset_id: number,
    team_id: number,
    type: string
}

interface IComparisonType {
    label: string,
    limit: any,
    metadata: object,
    value: string
}